import { inject, Injectable } from '@angular/core';
import { ENDPOINTS } from '../../../enums/endpoints.enum';
import { ODataCalibrationModel } from '../../../models/low-level/measurement-tool.model';
import { OnlineOnlyService } from '../../../services/online-only/online-only.service';
import { UriUtilitiesService } from '../../../services/uri-utilities/uri-utilities.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import {
    calibrationDateFailed,
    calibrationDateRequested,
    calibrationDateSucceeded,
} from '../actions/confirmation.actions';

/**
 * Owns the read-only lookups `ConfirmationNgrxService` used to perform inline — today just the
 * measurement-tool calibration date (`OnlineOnlyService`). It does not touch the confirmation slice:
 * it is a request/outcome pipeline whose only job is to keep the facade a pure dispatch-and-await
 * boundary, with the collaborator call and the response check living here instead.
 *
 * Kept separate from `ConfirmationSubmitEffects` because nothing here participates in the submit
 * pipeline, and that file is already the migration's largest.
 */
@Injectable()
export class ConfirmationQueryEffects {
    private readonly actions$ = inject(Actions);
    private readonly onlineOnlyService = inject(OnlineOnlyService);
    private readonly uriUtilities = inject(UriUtilitiesService);

    /**
     * One-shot OData read, unchanged from the legacy `getCalibrationDate` including its empty-response
     * error: `MeasToolSet` filtered by equipment, first record wins.
     */
    public loadCalibrationDate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(calibrationDateRequested),
            mergeMap(({ measurementToolId, correlationId }) => {
                let query = ENDPOINTS.MeasToolSet.toString();
                query = this.uriUtilities.appendFilterValue(query, `Equipment eq '${measurementToolId}'`);
                return this.onlineOnlyService.get(query).pipe(
                    map((response: ODataCalibrationModel[]) => {
                        if (response.length < 1) {
                            throw new Error(
                                'ConfirmationQueryEffects: loadCalibrationDate$: Could not get Calibration Date!',
                            );
                        }
                        return calibrationDateSucceeded({ correlationId, calibration: response[0] });
                    }),
                    catchError((error) => of(calibrationDateFailed({ correlationId, error: String(error) }))),
                );
            }),
        ),
    );
}
